// src/lib/projects.js
import supabase from "../supabase";
import { getAuthHeader } from "./authFetch";

/**
 * List projects the user can see:
 * - Admin => all projects in org
 * - Others => projects where user is in writers[] or editors[]
 */
export async function listProjectsForUser({ user_id, role, company_id }) {
  // 🚨 CRITICAL: Always require an org_id to prevent data leaks
  if (!company_id) return [];

  // Admins see all projects in THEIR org
  if ((role || "").toLowerCase() === "admin") {
    const { data, error } = await supabase
      .from("projects")
      .select("id, title, description")
      .eq("company_id", company_id) // ✅ Lock to Tenant
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  }

  // Non-admins: only projects where they are a member (also locked to org)
  const { data, error } = await supabase
    .from("projects")
    .select("id, title, project_members!inner(user_id)")
    .eq("company_id", company_id) // ✅ Lock to Tenant
    .eq("project_members.user_id", user_id)
    .order("created_at", { ascending: false });

  if (error) throw error;
  // strip the join payload
  return (data || []).map(({ id, title }) => ({ id, title }));
}

export async function createProject({
  title,
  organization,
  description,
  writers,
  editors,
  company_id,
  created_by,
}) {
  const { error } = await supabase.from("projects").insert({
    title,
    organization,
    description,
    writers: writers?.length ? writers : null,
    editors: editors?.length ? editors : null,
    company_id,
    created_by,
  });

  if (error) throw error;
  return true;
}

export async function listProjectDocuments(projectId) {
  const res = await fetch(`/api/projects/${projectId}/documents`, {
    headers: await getAuthHeader(),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.detail || "Failed to load documents");
  return json.documents || [];
}

export async function uploadProjectDocument(projectId, formData) {
  const res = await fetch(`/api/projects/${projectId}/documents`, {
    method: "POST",
    headers: await getAuthHeader(),
    body: formData,
  });

  const text = await res.text();

  let json;
  try {
    json = JSON.parse(text);
  } catch (e) {
    console.error("Upload returned non-JSON:", text.slice(0, 500));
    throw new Error("Upload failed: server returned HTML (check server logs).");
  }

  if (!res.ok) throw new Error(json?.detail || "Failed to upload document");
  return json.document;
}

export async function deleteProjectDocument(projectId, docId) {
  const res = await fetch(`/api/projects/${projectId}/documents/${docId}`, {
    method: "DELETE",
    headers: await getAuthHeader(),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.detail || "Failed to delete document");
  return true;
}

export async function updateProjectTeam(projectId, { writers, editors, approvers }) {
  const res = await fetch(`/api/projects/${projectId}/team`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthHeader()),
    },
    body: JSON.stringify({ writers, editors, approvers }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || "Failed to save project team");
  return json.project;
}
